import { parseAmount } from './number'

/**
 * A table's rows as the person reading it asked to see them: narrowed by what they typed or
 * chose in a column's filter, and sorted by the column whose heading they pressed (TRKC-110,
 * Sergio's item 5: every table filters and sorts by any column that holds information).
 *
 * <p>
 * Pure, so the rules are tested without a screen: the hook in `useTableView` holds the state,
 * and `components/TableHeads` draws the headings and the filter boxes.
 * </p>
 */

export type SortDirection = 'ascending' | 'descending'

export interface TableSort {
  column: string
  direction: SortDirection
}

/** What a column sorts by: text, a number, or an ISO date (which sorts as text). */
export type SortValue = string | number | null

export type ColumnFilter<Row> =
  /** The column's text holds what was typed, whatever its case or accents. */
  | { kind: 'text'; text: (row: Row) => string | null }
  /**
   * The column's value is the one chosen from a list. `values` are the choices the list
   * offers: a value an address carries that is not one of them is dropped on the way in.
   */
  | { kind: 'choice'; values: readonly string[]; matches: (row: Row, chosen: string) => boolean }
  /**
   * The column's figure is at least, or at most, the one typed. A row the column says nothing
   * about is kept only when `keepUnknown` says so: a load with no rate posted is negotiated,
   * not cheap, while a broker DAT has not scored is no broker with a good score.
   */
  | { kind: 'atLeast' | 'atMost'; value: (row: Row) => number | null; keepUnknown: boolean }
  /** The column's day is on or after the one chosen (ISO dates compare as text). */
  | { kind: 'onOrAfter'; value: (row: Row) => string | null }

export interface TableColumn<Row> {
  key: string
  /** Absent when the column does not sort — a tick box, a button. */
  sortValue?: (row: Row) => SortValue
  /** Absent when the column does not filter. */
  filter?: ColumnFilter<Row>
}

/** What each column's filter box holds, as typed: an empty box filters nothing. */
export type TableFilters = Readonly<Record<string, string>>

export interface TableViewState {
  sort: TableSort | null
  filters: TableFilters
}

export const NO_VIEW: TableViewState = { sort: null, filters: {} }

/** Text as a filter compares it: no case, no accents, single spaces. */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * A figure typed into a filter that is not one — "1..5" — filters nothing, and the box says
 * so: dropping it in silence would show every row under a filter that looks applied
 * (the house rule since GPT-5.6 Sol on #28).
 */
export function isUnreadableFigure(typed: string): boolean {
  return typed.trim() !== '' && parseAmount(typed.trim()) === null
}

/** Whether a filter box holds something that narrows the rows. */
function narrows<Row>(filter: ColumnFilter<Row>, typed: string | undefined): typed is string {
  if (typed === undefined || typed.trim() === '') {
    return false
  }

  return filter.kind === 'atLeast' || filter.kind === 'atMost' ? !isUnreadableFigure(typed) : true
}

/** How many columns narrow the rows right now. */
export function activeFilters<Row>(columns: readonly TableColumn<Row>[], filters: TableFilters): number {
  return columns.filter((column) => column.filter !== undefined && narrows(column.filter, filters[column.key])).length
}

function keeps<Row>(filter: ColumnFilter<Row>, row: Row, typed: string): boolean {
  switch (filter.kind) {
    case 'text':
      return fold(filter.text(row) ?? '').includes(fold(typed))
    case 'choice':
      return filter.matches(row, typed)
    case 'atLeast':
    case 'atMost': {
      const value = filter.value(row)
      if (value === null) {
        return filter.keepUnknown
      }
      const threshold = parseAmount(typed.trim()) as number
      return filter.kind === 'atLeast' ? value >= threshold : value <= threshold
    }
    case 'onOrAfter': {
      const value = filter.value(row)
      return value !== null && value >= typed
    }
  }
}

/** The rows every filter keeps, in the order they came. */
export function filterRows<Row>(rows: readonly Row[], columns: readonly TableColumn<Row>[], filters: TableFilters): Row[] {
  const applied = columns.flatMap((column) => {
    const typed = filters[column.key]
    return column.filter !== undefined && narrows(column.filter, typed) ? [{ filter: column.filter, typed }] : []
  })

  return applied.length === 0 ? [...rows] : rows.filter((row) => applied.every(({ filter, typed }) => keeps(filter, row, typed)))
}

/**
 * The rows sorted by one column. A row the column says nothing about goes last whichever way
 * the column is sorted: an unscored broker is neither the best nor the worst, and putting it
 * first under "highest" would read as the opposite.
 *
 * <p>
 * A table whose rows fall in groups — open postings, then expired, then withdrawn — gives each
 * row its group's `rank`, and a sorted column sorts within the groups: a row that is history
 * never sits among the current ones, whatever the column (TRKC-74, TRKC-90).
 * </p>
 */
export function sortRows<Row>(
  rows: readonly Row[],
  columns: readonly TableColumn<Row>[],
  sort: TableSort | null,
  compareText: (a: string, b: string) => number,
  rank?: (row: Row) => number,
): Row[] {
  const column = sort === null ? undefined : columns.find((candidate) => candidate.key === sort.column)

  if (sort === null || column?.sortValue === undefined) {
    return [...rows]
  }

  const valueOf = column.sortValue
  const sign = sort.direction === 'ascending' ? 1 : -1

  // The sort is stable, so rows that tie keep the order they came in, sorted either way.
  return rows
    .map((row) => ({ row, value: valueOf(row), group: rank?.(row) ?? 0 }))
    .sort((a, b) => {
      if (a.group !== b.group) {
        return a.group - b.group
      }

      if (a.value === null || b.value === null) {
        return a.value === b.value ? 0 : a.value === null ? 1 : -1
      }

      const order =
        typeof a.value === 'number' && typeof b.value === 'number'
          ? a.value - b.value
          : compareText(String(a.value), String(b.value))

      return sign * order
    })
    .map(({ row }) => row)
}

/**
 * The sort a heading's press asks for: ascending first, then descending, then the order the
 * rows came in — the book's, or DAT's on the paste.
 */
export function nextSort(current: TableSort | null, column: string): TableSort | null {
  if (current === null || current.column !== column) {
    return { column, direction: 'ascending' }
  }

  return current.direction === 'ascending' ? { column, direction: 'descending' } : null
}

/** The rows as the table shows them: filtered, then sorted. */
export function viewOf<Row>(
  rows: readonly Row[],
  columns: readonly TableColumn<Row>[],
  state: TableViewState,
  compareText: (a: string, b: string) => number,
  rank?: (row: Row) => number,
): Row[] {
  return sortRows(filterRows(rows, columns, state.filters), columns, state.sort, compareText, rank)
}

/** A view as one string, to tell two apart: the same sort and the same narrowing filters. */
export function viewKey(state: TableViewState): string {
  const filters = Object.entries(state.filters)
    .filter(([, typed]) => typed.trim() !== '')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return JSON.stringify([state.sort, filters])
}
